import React, { useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import type { PopperProps } from 'react-popper';
import { Popper } from 'react-popper';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import { canUseDOM } from '@swf/core/utils';

// Use PopperProps after material-ui update
interface OwnProps {
  createPortal?: boolean;
  isOpen: boolean;
  onClose?(): void;
}

// eslint-disable-next-line @typescript-eslint/ban-types
type Props = OwnProps & PopperProps<{}>;

const Tooltip: React.FC<Props> = ({ children, isOpen, createPortal = true, onClose, ...rest }) => {
  const node = useRef<HTMLElement>(null);
  const isMobile = useSelector(select.device.isMobile);

  // TODO: convert to usePopper hook
  const renderTooltip = useCallback(
    () => (
      <Popper {...rest} innerRef={node}>
        {children}
      </Popper>
    ),
    [children, rest]
  );

  const closeTooltip = useCallback(
    (e) => {
      if (!isOpen) {
        return;
      }

      const popper = node.current;

      if (onClose && popper && !popper.contains(e.target)) {
        onClose();
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    if (canUseDOM && onClose) {
      if (isMobile) {
        window.addEventListener('touchend', closeTooltip, false);
      } else {
        window.addEventListener('click', closeTooltip, false);
      }
    }
    return () => {
      if (canUseDOM && onClose) {
        if (isMobile) {
          window.removeEventListener('touchend', closeTooltip, false);
        } else {
          window.removeEventListener('click', closeTooltip, false);
        }
      }
    };
  }, [closeTooltip, isMobile, onClose]);

  if (!isOpen) {
    return null;
  }

  if (createPortal) {
    return canUseDOM
      ? ReactDOM.createPortal(renderTooltip(), document.getElementById('root') as HTMLElement)
      : null;
  }
  return renderTooltip();
};
export default Tooltip;
